import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import { Building2, Save, SlidersHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { supabase } from '../lib/supabaseClient';
import { fetchProfileLookups, hydrateProfile } from '../lib/profileLookups';

const DEFAULT_SETTINGS = {
  nama_sistem: 'Sistem Informasi Akreditasi',
  nama_kampus: 'Politeknik Negeri Manado',
  deskripsi_sistem:
    'Sistem informasi untuk pengelolaan, arsip, dan rekap data akreditasi program studi.',
  periode_akreditasi: '2025/2026',
  footer_text:
    'Copyright 2025 Politeknik Negeri Manado. Sistem Informasi Akreditasi Kampus.',
};

function SystemSettingsPage() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [settingId, setSettingId] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const loadSystemSettings = async () => {
    const { data, error } = await supabase
      .schema('akreditasi')
      .from('system_settings')
      .select('id, nama_sistem, nama_kampus, deskripsi_sistem, periode_akreditasi, footer_text')
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(`Gagal mengambil pengaturan sistem: ${error.message}`);
    }

    if (!data) {
      setSettingId(null);
      setFormData(DEFAULT_SETTINGS);
      return;
    }

    setSettingId(data.id);
    setFormData({
      nama_sistem: data.nama_sistem || DEFAULT_SETTINGS.nama_sistem,
      nama_kampus: data.nama_kampus || DEFAULT_SETTINGS.nama_kampus,
      deskripsi_sistem: data.deskripsi_sistem || DEFAULT_SETTINGS.deskripsi_sistem,
      periode_akreditasi:
        data.periode_akreditasi || DEFAULT_SETTINGS.periode_akreditasi,
      footer_text: data.footer_text || DEFAULT_SETTINGS.footer_text,
    });
  };

  const initPage = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate('/');
      return;
    }

    const { data: profileData, error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error || !profileData) {
      setErrorMsg('Profile user tidak ditemukan.');
      setLoading(false);
      return;
    }

    if (!profileData.is_active) {
      setErrorMsg('Akun nonaktif.');
      setLoading(false);
      return;
    }

    if (profileData.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }

    try {
      const lookups = await fetchProfileLookups(supabase);
      setProfile(hydrateProfile(profileData, lookups));
    } catch (lookupError) {
      setErrorMsg(lookupError.message);
      setLoading(false);
      return;
    }

    try {
      await loadSystemSettings();
    } catch (settingsError) {
      setErrorMsg(settingsError.message);
    }

    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    initPage();
  }, [initPage]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    const payload = {
      nama_sistem: formData.nama_sistem.trim(),
      nama_kampus: formData.nama_kampus.trim(),
      deskripsi_sistem: formData.deskripsi_sistem.trim(),
      periode_akreditasi: formData.periode_akreditasi.trim(),
      footer_text: formData.footer_text.trim(),
      updated_at: new Date().toISOString(),
    };

    if (!payload.nama_sistem || !payload.nama_kampus) {
      setErrorMsg('Nama sistem dan nama kampus wajib diisi.');
      setSaving(false);
      return;
    }

    if (settingId) {
      const { data, error } = await supabase
        .schema('akreditasi')
        .from('system_settings')
        .update(payload)
        .eq('id', settingId)
        .select('id, nama_sistem, nama_kampus, deskripsi_sistem, periode_akreditasi, footer_text')
        .maybeSingle();

      if (error) {
        setErrorMsg(error.message);
        setSaving(false);
        return;
      }

      setSettingId(data.id);
    } else {
      const { data, error } = await supabase
        .schema('akreditasi')
        .from('system_settings')
        .insert([payload])
        .select('id, nama_sistem, nama_kampus, deskripsi_sistem, periode_akreditasi, footer_text')
        .maybeSingle();

      if (error) {
        setErrorMsg(error.message);
        setSaving(false);
        return;
      }

      setSettingId(data.id);
    }

    setFormData(payload);
    setSuccessMsg('Pengaturan sistem berhasil disimpan.');
    setSaving(false);
  };

  if (loading) {
    return (
      <DashboardLayout
        title="Pengaturan Sistem"
        breadcrumbs={['Dashboard', 'Pengaturan', 'Sistem']}
        profile={profile}
        activeMenu="settings"
      >
        <div className="page-loading-v2">
          <Spinner animation="border" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Pengaturan Sistem"
      breadcrumbs={['Dashboard', 'Pengaturan', 'Sistem']}
      profile={profile}
      activeMenu="settings"
    >
      <Container fluid className="p-0">
        {successMsg && <Alert variant="success">{successMsg}</Alert>}
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

        <Row className="g-4">
          <Col xl={8}>
            <Card className="content-card-v2 settings-card">
              <Card.Body>
                <div className="section-head-v2 mb-3">
                  <div>
                    <h4 className="section-title-v2">Identitas Sistem</h4>
                  </div>
                  <div className="settings-menu-icon compact">
                    <SlidersHorizontal size={22} />
                  </div>
                </div>

                <Form onSubmit={handleSubmit}>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label>Nama Sistem</Form.Label>
                      <Form.Control
                        name="nama_sistem"
                        value={formData.nama_sistem}
                        onChange={handleChange}
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Nama Kampus</Form.Label>
                      <Form.Control
                        name="nama_kampus"
                        value={formData.nama_kampus}
                        onChange={handleChange}
                        required
                      />
                    </Col>

                    <Col md={12}>
                      <Form.Label>Deskripsi Sistem</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="deskripsi_sistem"
                        value={formData.deskripsi_sistem}
                        onChange={handleChange}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Periode Akreditasi</Form.Label>
                      <Form.Control
                        name="periode_akreditasi"
                        value={formData.periode_akreditasi}
                        onChange={handleChange}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Footer Text</Form.Label>
                      <Form.Control
                        name="footer_text"
                        value={formData.footer_text}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  <div className="criterion-form-actions">
                    <Button className="save-btn-v2" type="submit" disabled={saving}>
                      <Save size={16} className="me-2" />
                      {saving ? 'Menyimpan...' : 'Simpan Tampilan'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="content-card-v2 settings-card">
              <Card.Body>
                <div className="section-head-v2 mb-3">
                  <div>
                    <h4 className="section-title-v2">Preview Identitas Sistem</h4>
                  </div>
                  <div className="settings-menu-icon compact">
                    <Building2 size={22} />
                  </div>
                </div>

                <div className="system-preview-panel">
                  <div className="system-preview-badge">Akreditasi</div>
                  <h3>{formData.nama_sistem || '-'}</h3>
                  <p>{formData.deskripsi_sistem || '-'}</p>
                </div>

                <div className="archive-side-meta">
                  <div>
                    <strong>Nama Kampus</strong> : {formData.nama_kampus || '-'}
                  </div>
                  <div>
                    <strong>Periode</strong> : {formData.periode_akreditasi || '-'}
                  </div>
                  <div>
                    <strong>Status Data</strong> : {settingId ? 'Tersimpan' : 'Belum tersimpan'}
                  </div>
                </div>

                <div className="settings-preview-footer">
                  {formData.footer_text || '-'}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </DashboardLayout>
  );
}

export default SystemSettingsPage;
